import React, { useRef } from "react";
import { CSSTransition } from "react-transition-group";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import {
  InstaIcon,
  FacebookIcon,
  TwitterIcon,
  SubscribeIcon,
  YouTubeIcon,
  DonateIcon,
} from "../../assets/FooterIcons";
import { EGWLogo, ChevronDownIcon, ChevronUpIcon } from "../../assets/CommonIcons";
import ViewMode from "../views/ViewMode";
import { useViewMode } from "../../hooks";
import TextButton from "../views/TextButton";
import { EXTERNAL_URLS, URLS } from "../../shared/utils/url";
import { scrollToElement } from "../../utils/Utils";
import VersionView from "../VersionView";
import { actionUpdateSetting } from "../../redux/actions";
import { Settings } from "../../utils/Settings";
import { IconButton } from "../views";

import "./Footer.scss";

const socialIcons = [
  [
    {
      icon: FacebookIcon,
      href: "https://www.facebook.com/Ellen-G-White-Writings-141154832624917/",
      ariaLabel: "_FB_",
      title: "socialFacebookTitle",
    },
    {
      icon: InstaIcon,
      href: "https://www.instagram.com/egwwritings/",
      ariaLabel: "_IG_",
      title: "socialInstagramTitle",
    },
    {
      icon: TwitterIcon,
      href: "https://twitter.com/ellenwhite",
      ariaLabel: "_TW_",
      title: "socialTwitterTitle",
    },
  ],
  [
    {
      icon: YouTubeIcon,
      href: "https://www.youtube.com/channel/UCJlRjlukZiCk3YFuOP4sz9w",
      ariaLabel: "_YT_",
      title: "socialYoutubeTitle",
    },
    {
      icon: SubscribeIcon,
      href: EXTERNAL_URLS.subscription,
      ariaLabel: "_SUB_",
      title: "socialSubscribeTitle",
    },
    {
      icon: DonateIcon,
      href: EXTERNAL_URLS.donate,
      ariaLabel: "donate",
      title: "socialDonateTitle",
    },
  ],
];

const infoLinks = [
  {
    label: "Contact",
    href: URLS.contactus,
  },
  {
    label: "Feedback",
    href: URLS.contactus,
  },
  {
    label: "Visit",
    href: "https://ellenwhite.org/visitors-center",
  },
  {
    label: "Copyright Notice",
    href: URLS.copirightNotice,
  },
  {
    label: "Support",
    href: EXTERNAL_URLS.donate,
  },
  {
    label: "Privacy Policy",
    href: URLS.privacyPolicy,
  },
];

const siteLinks = [
  {
    label: "ellenwhite.org",
    href: "http://ellenwhite.org/",
  },
  {
    label: "whiteestate.org",
    href: "https://whiteestate.org/",
  },
  {
    label: "egwwritings.org",
    href: "https://egwwritings.org/",
  },
  {
    label: "adventistarchives.org",
    href: "https://www.adventistarchives.org/",
  },
  {
    label: "adventistdigitallibrary.org",
    href: "https://adventistdigitallibrary.org",
  },
  {
    label: "adventistheritage.org",
    href: "https://www.adventistheritage.org/",
  },
];

const AdventistIcon = () => (
  <svg width="63" height="63" viewBox="0 0 63 63" className="footerADVIcon">
    <circle fill={"var(--footer-logo-circle)"} cx="31.5" cy="31.5" r="31.5" />
    <path
      fill={"var(--footer-logo-color)"}
      d="M34.5976 32.1387C33.07 33.9311 32.3211 34.7922 31.9317 36.9887C31.9018 37.1469 31.752 37.1469 31.752 36.9887V34.3001C31.752 32.578 33.055 31.0141 34.6126 29.2041L35.8557 27.7456C37.608 25.6896 38.0274 23.3173 36.6345 21.9115C36.5147 21.7884 36.5746 21.6479 36.7244 21.7357C38.4617 22.6671 39.8247 26.0234 36.8592 29.5204L34.5976 32.1387ZM31.737 39.0096V41.6982C31.737 41.8563 31.9018 41.8563 31.9167 41.6982C32.3061 39.5192 33.04 38.6406 34.5827 36.8306L37.009 33.9838C39.9744 30.5044 40.4986 27.1305 38.9859 24.9515C38.8811 24.8109 38.7763 24.8988 38.8362 25.0745C39.8396 27.728 37.4433 30.5396 36.305 31.8751L34.5976 33.896C33.04 35.7235 31.737 37.305 31.737 39.0096ZM25.1471 30.3463C22.1816 33.8257 23.5445 37.1996 25.2819 38.1309C25.4317 38.2188 25.4916 38.0607 25.3717 37.9552C23.9789 36.5494 24.3982 34.1595 26.1506 32.1211L31.093 26.3222C32.6506 24.4946 33.9536 22.9307 33.9536 21.2261V18.5199C33.9536 18.3618 33.7889 18.3618 33.7739 18.5199C33.3845 20.6989 32.6506 21.56 31.108 23.37L25.1471 30.3463ZM33.9536 16.4991V13.7929C33.9536 13.6348 33.7889 13.6348 33.7739 13.7929C33.3845 15.9719 32.6506 16.8506 31.108 18.6605L24.9823 25.8301C22.0169 29.3095 21.4927 32.6834 23.0054 34.8624C23.1102 35.003 23.215 34.9152 23.1551 34.7394C22.1517 32.086 24.548 29.2744 25.6863 27.9389L31.093 21.5951C32.6506 19.7852 33.9387 18.2212 33.9536 16.4991ZM24.3233 21.9115C21.7623 24.8988 21.4028 28.0794 21.957 30.153C22.0019 30.3287 22.1367 30.3463 22.1367 30.153C22.1217 28.4133 22.556 26.9196 25.2519 23.7566L31.093 16.9033C32.6506 15.0757 33.9536 13.5118 33.9536 11.8072V9.11861C33.9536 8.96046 33.7889 8.96046 33.7739 9.11861C33.3845 11.2976 32.6506 12.1587 31.108 13.9686L24.3233 21.9115ZM27.9029 43.6136C27.8579 43.6136 27.813 43.5609 27.813 43.5082V41.9442C27.813 41.8915 27.8579 41.8388 27.9029 41.8388H30.1494C30.1944 41.8388 30.2393 41.786 30.2393 41.7333C30.2393 40.3451 29.3407 39.3962 25.7462 40.1167L17.988 41.6806C17.988 41.6806 12.087 48.6042 12.0421 48.6745C11.9972 48.7272 11.9822 48.8151 12.0271 48.8678C12.0571 48.9029 12.102 48.9205 12.1469 48.9029C12.2518 48.8854 25.0422 46.3022 25.0422 46.3022C27.9778 45.7223 29.5803 47.3566 30.0596 48.9029C30.1045 49.0435 30.2393 49.0259 30.2393 48.8854V43.7015C30.2393 43.6487 30.1944 43.596 30.1494 43.596L27.9029 43.6136ZM37.1587 40.3099L34.5976 43.3149C33.04 45.1424 31.752 46.7064 31.752 48.4109V48.8854C31.752 49.0084 31.8868 49.0435 31.9317 48.9029C32.411 47.3741 34.0135 45.7223 36.9491 46.3022C36.9491 46.3022 49.7395 48.9029 49.8444 48.9029C49.9043 48.9205 49.9791 48.8678 49.9941 48.7975C50.0091 48.7448 49.9941 48.6921 49.9642 48.6569C49.9192 48.5866 44.0033 41.663 44.0033 41.663L37.1587 40.3099ZM40.0194 29.6961C39.9744 29.5204 39.8396 29.5028 39.8396 29.6961C39.8546 31.4358 39.4203 32.9295 36.7244 36.0925L34.5827 38.5878C33.6092 39.7301 32.7405 40.7668 32.2163 41.8212H34.3281L34.5827 41.5225L37.653 37.9201C40.2141 34.9503 40.5735 31.7697 40.0194 29.6961ZM31.737 46.3901C31.737 46.5482 31.9018 46.5482 31.9167 46.3901C32.0665 45.3709 32.426 44.4219 32.9502 43.6136H31.8269C31.7819 43.6136 31.737 43.6663 31.737 43.719V46.3901Z"
    />
  </svg>
);

const Footer = () => {
  const showContentTimerRef = useRef(0);
  const { isMobile, isMobileOrTablet, textMode, zoom } = useViewMode();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isShowFooter = useSelector((state) => state.settings.isShowFooter);
  const isShowFooterContent = useSelector((state) => state.settings.isShowFooterContent);

  const specialZoomClass = () => {
    const width = window.innerWidth;

    if (textMode) {
      return (
        (zoom >= 100 && width <= 1200) ||
        (zoom >= 110 && width <= 1300) ||
        (zoom >= 120 && width <= 1350) ||
        (zoom >= 130 && width <= 1400) ||
        (zoom >= 140 && width <= 1500) ||
        (zoom >= 150 && width <= 1650) ||
        (zoom >= 160 && width <= 1750)
      );
    }

    return (
      (zoom >= 100 && width <= 1150) ||
      (zoom >= 110 && width <= 1250) ||
      (zoom >= 120 && width <= 1300) ||
      (zoom >= 130 && width <= 1400) ||
      (zoom >= 140 && width <= 1500) ||
      (zoom >= 150 && width <= 1700) ||
      (zoom >= 160 && width <= 1750)
    );
  };

  const specialWrapClass = () => {
    const width = window.innerWidth;
    if (textMode) {
      return (
        (zoom >= 100 && width <= 1450) ||
        (zoom >= 110 && width <= 1550) ||
        (zoom >= 120 && width <= 1600) ||
        (zoom >= 130 && width <= 1700) ||
        (zoom >= 140 && width <= 1800) ||
        (zoom >= 150 && width <= 1950) ||
        (zoom >= 160 && width <= 2050)
      );
    }

    return (
      (zoom >= 100 && width <= 1250) ||
      (zoom >= 110 && width <= 1350) ||
      (zoom >= 120 && width <= 1500) ||
      (zoom >= 130 && width <= 1600) ||
      (zoom >= 140 && width <= 1700) ||
      (zoom >= 150 && width <= 1850) ||
      (zoom >= 160 && width <= 2000)
    );
  };

  const handleShowContent = () => {
    if (isMobileOrTablet && !isShowFooterContent) {
      if (showContentTimerRef.current) {
        clearTimeout(showContentTimerRef.current);
      }

      showContentTimerRef.current = setTimeout(() => {
        scrollToElement("footer");
      }, 0);
    }

    dispatch(actionUpdateSetting(Settings.isShowFooterContent.id, !isShowFooterContent));
  };

  const renderSocialIcon = ({ icon, ariaLabel, title, href }, index) => {
    return (
      <a aria-label={t(title)} key={index} href={href} target="_blank" rel="noopener noreferrer">
        <IconButton icon={icon} className="footerIcon" title={t(title)} ariaLabel={t(ariaLabel)} />
      </a>
    );
  };

  const renderSocialIcons = () => {
    if (textMode) {
      // render all icons in one row
      return (
        <div className="footerIconsContainer">
          {socialIcons.map((icons, key) => {
            return <React.Fragment key={key}>{icons.map(renderSocialIcon)}</React.Fragment>;
          })}
        </div>
      );
    }

    return socialIcons.map((icons, key) => {
      // render each set of icons in a different row
      return (
        <div key={key} className="footerIconsContainer">
          {icons.map(renderSocialIcon)}
        </div>
      );
    });
  };

  const year = new Date().getFullYear().toString();

  const renderCopyright = () => {
    const copyright = isMobile
      ? "Copyright, &copy; " + year + ".<br/> Ellen G. White&reg; Estate Inc."
      : "Copyright, &copy; " + year + ". Ellen G. White&reg; Estate Inc.";
    const ChevronIcon = isShowFooterContent ? ChevronUpIcon : ChevronDownIcon;
    return (
      <div className="logoFooterContainer">
        <div className="footerCopyright">
          <EGWLogo className="logoFooterIcon" />
          <span className="footerSmallLogo">&reg;</span>
        </div>
        <div className="footerCopyrightText">
          <Link to="/legal-copyright-notice" dangerouslySetInnerHTML={{ __html: copyright }} />
          {!isMobile && <br />}
          {!isMobile && (
            <a
              href="https://www.google.com/maps/place/Ellen+G+White+Estate,+Inc./@39.061175,-76.9679917,17z/data=!3m1!4b1!4m5!3m4!1s0x89b7c51fc029a6a7:0x8214b4d757510e16!8m2!3d39.061175!4d-76.965803"
              target="_blank"
              rel="noopener noreferrer">
              12501 Old Columbia Pike, Silver Spring, MD 20904, USA | Ph# 1.301.680.6540
              <br />
            </a>
          )}

          <VersionView link />
          {isMobileOrTablet && (
            <TextButton className="btn-more" onClick={handleShowContent}>
              {isShowFooterContent ? t("less") : t("more")}
              <ChevronIcon className="footerIconRightArrow footerIcon" />
            </TextButton>
          )}
        </div>
      </div>
    );
  };

  const renderLinks = (links, title) => {
    return (
      <div className="footerLinksContainer">
        <div className="footerLinksTitle">{t(title)}</div>
        <div className="footerLinksGrid">
          {links.map((item, index) => {
            if (item.href.indexOf("http") === 0) {
              return (
                <a key={index} href={item.href} target="_blank" rel="noopener noreferrer">
                  {item.label}
                </a>
              );
            }
            return (
              <Link key={index} to={item.href}>
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdventistBlock = (
    <div className="adventistContainer">
      <a
        className="adventistIconContainer"
        href="https://www.adventist.org/en/"
        target="_blank"
        rel="noopener noreferrer">
        {!textMode && <AdventistIcon />}

        <span>adventist.org</span>
      </a>
    </div>
  );

  const footerLinksWrapper = (
    <div className="footerLinksWrapper">
      {renderLinks(infoLinks, "info")}
      {renderLinks(siteLinks, "sites")}
    </div>
  );

  if (!isShowFooter) {
    return null;
  }

  return (
    <footer
      className={classNames("footerMain", {
        withZoom: specialZoomClass(),
        withWrap: specialWrapClass(),
      })}>
      {!isMobileOrTablet ? (
        <React.Fragment>
          <div className="footerIconsWrapper">
            {renderSocialIcons()}
            <ViewMode />
          </div>
          {renderCopyright()}
          {footerLinksWrapper}
          {renderAdventistBlock}
        </React.Fragment>
      ) : (
        <div className={classNames("footerRow", { collapsed: isShowFooterContent })}>
          {renderCopyright()}
          <CSSTransition
            in={isShowFooterContent}
            mountOnEnter
            unmountOnExit
            timeout={300}
            classNames="swap-down">
            <React.Fragment>
              <div className="footerContent">
                <div className="footerIconsWrapper">
                  {renderSocialIcons()}
                  <ViewMode />
                </div>
                {footerLinksWrapper}
              </div>
              {renderAdventistBlock}
            </React.Fragment>
          </CSSTransition>
        </div>
      )}
    </footer>
  );
};

export default Footer;
